import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../legal/legal.css';
import { StoreBadges } from './home/HomeArt';
import { storeUrl } from '../legal/appLinks';
import { useT } from '../i18n/I18nProvider';

// ─────────────────────────────────────────────────────────────────────────────
// AppDownloadModal — the "please use the app" interstitial.
//
// The website is a companion experience while it is still under development;
// the native app is the real product. Whenever a visitor opens an event detail
// page or a ticket page (i.e. clicks an event card or an event's tickets), this
// sheet slides up over the content and invites them to download the app instead.
//
// Behaviour:
//   • Opens on mount and re-opens on every route change of the host page — it
//     is deliberately NOT persisted, so it appears each time (per the product
//     requirement), on every screen size.
//   • Bottom sheet on small screens, centered slide-up dialog on desktop.
//   • Closable via the ✕ button, the backdrop, or the Escape key — the visitor
//     is never trapped, but there is no web-ticket continuation CTA.
//   • Suppressed inside in-app WebViews (?webview=1 / ?embed=1 / remembered
//     session flag) where promoting the app makes no sense.
//   • `deepLink` (optional): a custom-scheme URL (e.g. mainevent://event/123)
//     rendered as an "Open in the app" primary action with store fallback.
//
// Accessibility: role="dialog" + aria-modal, labelled by the title, focus moves
// to the dialog on open, background scroll is locked while it is visible.
// ─────────────────────────────────────────────────────────────────────────────

function isEmbeddedWebView(search: string): boolean {
    const params = new URLSearchParams(search);
    if (params.get('webview') === '1' || params.get('embed') === '1' || params.get('mode') === 'app') {
        return true;
    }
    try {
        return sessionStorage.getItem('me.embed') === '1';
    } catch {
        return false;
    }
}

export default function AppDownloadModal({ deepLink, reopenKey = 0 }: { deepLink?: string; reopenKey?: number }) {
    const { t } = useT();
    const { pathname, search } = useLocation();
    const [open, setOpen] = useState(false);
    const dialogRef = useRef<HTMLDivElement>(null);

    const embedded = isEmbeddedWebView(search);

    // (Re-)arm on every navigation so the sheet pops up each time an event or
    // ticket page is opened, including in-page jumps to related events.
    useEffect(() => {
        if (!embedded) setOpen(true);
    }, [pathname, embedded, reopenKey]);

    const close = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('keydown', onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, close]);

    const openInApp = () => {
        const target = deepLink || storeUrl();
        const start = Date.now();
        const fallback = window.setTimeout(() => {
            if (Date.now() - start < 2200) {
                window.location.href = storeUrl();
            }
        }, 1400);

        const onHide = () => {
            if (document.visibilityState === 'hidden') window.clearTimeout(fallback);
        };
        document.addEventListener('visibilitychange', onHide, { once: true });

        try {
            window.location.href = target;
        } catch {
            window.location.href = storeUrl();
        }
    };

    if (!open || embedded) return null;

    return (
        <div className="se-appgate-backdrop" onClick={close} role="presentation">
            <div
                className="se-appgate-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="se-appgate-title"
                onClick={(e) => e.stopPropagation()}
                ref={dialogRef}
                tabIndex={-1}
            >
                <span className="se-appgate-grabber" aria-hidden="true" />
                <button type="button" className="se-appgate-close" onClick={close} aria-label={t('appGate.close')}>
                    ✕
                </button>

                <div className="se-appgate-badge" aria-hidden="true">
                    <img src="/mainevents_icon.png" alt="" />
                </div>

                <span className="se-hc-eyebrow se-appgate-eyebrow">{t('appGate.eyebrow')}</span>
                <h2 id="se-appgate-title">{t('appGate.title')}</h2>
                <p className="se-appgate-text">{t('appGate.text')}</p>

                <div className="se-appgate-actions">
                    <button type="button" className="se-appgate-primary" onClick={openInApp}>
                        {t('appGate.open')}
                    </button>
                    <span className="se-appgate-hint">{t('appGate.badgesHint')}</span>
                    <StoreBadges className="se-appgate-badges" />
                </div>
            </div>
        </div>
    );
}
